import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CmsFormPanel, PanelContent, PanelHeader, PanelTitle } from '@/components/shared/CmsFormPanel';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, ExternalLink } from 'lucide-react';
import { getApiBaseUrl } from '@/services/apiClient';
import { ConfirmDialog } from '@/components/shared';

const getSponsorImageSrc = (imageUrl: string, isExternalImage: boolean) => {
  if (!imageUrl) return '';
  if (isExternalImage) return imageUrl;
  return `${getApiBaseUrl()}${imageUrl}`;
};

interface SponsorsFormDialogProps {
  isDialogOpen: boolean;
  setIsDialogOpen: (open: boolean) => void;
  editingSponsor: any;
  useExternalImage: boolean;
  setUseExternalImage: (val: boolean) => void;
  formData: {
    name: string;
    imageUrl: string;
    isExternalImage: boolean;
    websiteUrl: string;
    type: string;
    sortOrder: number;
  };
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  uploading: boolean;
  handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent) => void;
  deleteTarget: any;
  setDeleteTarget: (val: any) => void;
  confirmDelete: () => void;
}

const SponsorsFormDialog: React.FC<SponsorsFormDialogProps> = ({
  isDialogOpen, setIsDialogOpen, editingSponsor, useExternalImage, setUseExternalImage,
  formData, setFormData, uploading, handleImageUpload, handleSubmit,
  deleteTarget, setDeleteTarget, confirmDelete,
}) => (
  <>
    <CmsFormPanel open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <PanelContent>
        <PanelHeader>
          <PanelTitle>
            {editingSponsor ? 'Уредете спонзор' : 'Додадете нов спонзор'}
          </PanelTitle>
        </PanelHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Име на спонзор *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData((prev: any) => ({ ...prev, name: e.target.value }))}
              placeholder="Име на спонзорот"
              required
            />
          </div>
          <div>
            <Label htmlFor="type">Тип</Label>
            <Select
              value={formData.type}
              onValueChange={(val) => setFormData((prev: any) => ({ ...prev, type: val }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Sponsor">Спонзор</SelectItem>
                <SelectItem value="Partner">Партнер</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Слика</Label>
            <div className="flex gap-2 mt-1 mb-2">
              <Button
                type="button"
                variant={!useExternalImage ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setUseExternalImage(false);
                  setFormData((prev: any) => ({ ...prev, isExternalImage: false }));
                }}
                className="flex items-center gap-1"
              >
                <Upload className="w-3 h-3" />
                Прикачи слика
              </Button>
              <Button
                type="button"
                variant={useExternalImage ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setUseExternalImage(true);
                  setFormData((prev: any) => ({ ...prev, isExternalImage: true }));
                }}
                className="flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                Надворешен линк
              </Button>
            </div>

            {useExternalImage ? (
              <Input
                value={formData.imageUrl}
                onChange={(e) => setFormData((prev: any) => ({ ...prev, imageUrl: e.target.value }))}
                placeholder="https://example.com/logo.png"
              />
            ) : (
              <div className="space-y-2">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                  onChange={handleImageUpload}
                  className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20"
                  disabled={uploading}
                />
                {uploading && <p className="text-sm text-primary">Се прикачува...</p>}
                {formData.imageUrl && !useExternalImage && (
                  <p className="text-xs text-green-600 truncate">✓ {formData.imageUrl}</p>
                )}
              </div>
            )}

            {formData.imageUrl && (
              <div className="mt-2 w-20 h-20 bg-muted rounded-lg flex items-center justify-center overflow-hidden">
                <img
                  src={getSponsorImageSrc(formData.imageUrl, useExternalImage)}
                  alt="Preview"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="websiteUrl">Веб страница</Label>
            <Input
              id="websiteUrl"
              value={formData.websiteUrl}
              onChange={(e) => setFormData((prev: any) => ({ ...prev, websiteUrl: e.target.value }))}
              placeholder="https://www.sponsor.com"
            />
          </div>
          <div>
            <Label htmlFor="sortOrder">Редослед на прикажување</Label>
            <Input
              id="sortOrder"
              type="number"
              min="0"
              value={formData.sortOrder}
              onChange={(e) => setFormData((prev: any) => ({ ...prev, sortOrder: Number(e.target.value) }))}
            />
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
              Откажи
            </Button>
            <Button type="submit" disabled={uploading}>
              {editingSponsor ? 'Зачувај' : 'Креирај'}
            </Button>
          </div>
        </form>
      </PanelContent>
    </CmsFormPanel>
    <ConfirmDialog
      open={deleteTarget !== null}
      onOpenChange={(open) => !open && setDeleteTarget(null)}
      title="Избриши спонзор"
      description="Дали сте сигурни дека сакате да го избришете овој спонзор?"
      onConfirm={confirmDelete}
    />
  </>
);

export default SponsorsFormDialog;
